import { useEffect, useMemo, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Plug, Zap, Check, ShieldCheck, Eye,
  Workflow, GitBranch, Network, Map as MapIcon, Wand2,
} from "lucide-react";
import { IntegrationFlowGraph } from "../components/admin/IntegrationFlowGraph";
import { FlowGallery } from "../components/admin/FlowGallery";
import { OntologyMap } from "../components/integrations/OntologyMap";
import { AgenticMesh } from "../components/integrations/AgenticMesh";
import { AGENTIC_INTEGRATIONS } from "../data/agenticIntegrations";
import { PRECONFIGURED_FLOWS } from "../data/preconfiguredFlows";
import { getSession } from "../lib/session";
import { cls } from "../lib/cls";
import Onboard from "./Onboard";

/* Multi-tab Integrations page — Apple-style segmented control with
   pill-shaped indicator, framer-motion crossfade between sections.
   Counts come from data files, never hardcoded (CLAUDE.md §1).
   Onboarding lives here as a tab (was a separate top-nav entry —
   user feedback: "remove duplicate tabs on integration and
   onboarding"). Mesh / Ontology / Topology gated behind isAdmin so
   normal users see only the two relevant tabs (Connect + Onboard). */

const APP_COUNT  = AGENTIC_INTEGRATIONS.length;
const FLOW_COUNT = PRECONFIGURED_FLOWS.length;

type Tab = "onboard" | "flows" | "mesh" | "ontology" | "topology";

interface TabDef {
  id: Tab;
  label: string;
  sub: string;
  icon: JSX.Element;
  badge?: string;
  /** When true, only render when the session is admin. Keeps the
   *  normal-user view at the requested 2 tabs (Onboard + Flows). */
  adminOnly?: boolean;
}

const ALL_TABS: ReadonlyArray<TabDef> = [
  { id: "onboard",  label: "Onboard",       sub: "ingest brief → typed project graph", icon: <Wand2     className="w-3.5 h-3.5" /> },
  { id: "flows",    label: "Connect",       sub: "preconfigured automations", icon: <Workflow  className="w-3.5 h-3.5" />, badge: String(FLOW_COUNT) },
  { id: "mesh",     label: "Agentic mesh",  sub: "agents · protocols · stores", icon: <GitBranch className="w-3.5 h-3.5" />, adminOnly: true },
  { id: "ontology", label: "Ontology",      sub: "typed classes · W3C imports", icon: <Network   className="w-3.5 h-3.5" />, adminOnly: true },
  { id: "topology", label: "Topology",      sub: "live integration map",       icon: <MapIcon   className="w-3.5 h-3.5" />, adminOnly: true },
];

function readTabFromHash(visible: ReadonlyArray<TabDef>): Tab {
  if (typeof window === "undefined") return visible[0]?.id ?? "onboard";
  const h = window.location.hash.replace(/^#/, "");
  return (visible.find((t) => t.id === h)?.id) ?? visible[0]?.id ?? "onboard";
}

export default function IntegrationsPage(): JSX.Element {
  const isAdmin = getSession()?.isAdmin === true;
  // Normal users see 2 tabs (Onboard + Connect). Admins see all 5.
  const TABS = useMemo<ReadonlyArray<TabDef>>(
    () => ALL_TABS.filter((t) => isAdmin || !t.adminOnly),
    [isAdmin],
  );
  const [tab, setTab] = useState<Tab>(() => readTabFromHash(TABS));

  useEffect(() => {
    if (typeof window === "undefined") return;
    window.location.hash = tab;
  }, [tab]);

  useEffect(() => {
    function onHash(): void { setTab(readTabFromHash(TABS)); }
    window.addEventListener("hashchange", onHash);
    return () => window.removeEventListener("hashchange", onHash);
  }, [TABS]);

  const current = TABS.find((t) => t.id === tab) ?? TABS[0]!;

  return (
    <div className="max-w-[1280px] mx-auto px-4 sm:px-8 py-10 space-y-5">
      {/* Hero */}
      <header className="relative rounded-3xl border border-border/70 bg-gradient-to-br from-foyr/10 via-cream to-sea/5 px-6 py-7 overflow-hidden shadow-[0_1px_3px_rgba(0,0,0,0.04),0_12px_36px_-18px_rgba(0,0,0,0.10)]">
        <div className="absolute top-0 right-0 w-64 h-64 rounded-full bg-foyr/10 blur-3xl -translate-y-1/3 translate-x-1/3 pointer-events-none" />
        <div className="absolute bottom-0 left-0 w-48 h-48 rounded-full bg-sea/10 blur-3xl translate-y-1/3 -translate-x-1/3 pointer-events-none" />
        <div className="relative">
          <div className="eyebrow inline-flex items-center gap-1.5">
            <Plug className="w-3 h-3" /> Integrations
          </div>
          <h1 className="font-newyork display-1 mt-1 text-ink-950 leading-[1.04] tracking-tight">
            {FLOW_COUNT} flows. {APP_COUNT} apps. One picture.
          </h1>
          <p className="text-[14px] text-ink-700 mt-2 max-w-2xl leading-relaxed">
            Every flow shows the apps it touches.{" "}
            {isAdmin ? "Click any chip to connect or disconnect." : "Connecting and configuring apps is admin-only."}
          </p>
        </div>
      </header>

      {/* Admin / view-only status row */}
      {isAdmin ? (
        <div className="rounded-xl border border-foyr/30 bg-gradient-to-br from-foyr/10 via-cream to-cream-dark/30 px-3 py-2.5 flex items-center gap-2 flex-wrap shadow-sm">
          <Zap className="w-3.5 h-3.5 text-foyr" />
          <span className="text-[12px] text-ink-950 font-medium">
            Already running. Just turn on the apps you use.
          </span>
          <Check className="w-3.5 h-3.5 text-fern ml-auto" />
        </div>
      ) : (
        <div className="rounded-xl border border-mulberry/30 bg-mulberry/5 px-3 py-2.5 flex items-center gap-2 flex-wrap">
          <ShieldCheck className="w-3.5 h-3.5 text-mulberry" />
          <span className="text-[12px] text-ink-950 font-medium">
            View-only · admin rights required to connect apps or toggle flows.
          </span>
          <Eye className="w-3.5 h-3.5 text-ink-500 ml-auto" />
        </div>
      )}

      {/* Apple-style segmented tab bar */}
      <nav
        role="tablist"
        aria-label="Integration views"
        className="rounded-2xl border border-border/70 bg-cream/80 backdrop-blur-md px-1.5 py-1.5 inline-flex gap-1 shadow-[0_1px_2px_rgba(0,0,0,0.04)]"
      >
        {TABS.map((t) => {
          const active = t.id === tab;
          return (
            <button
              key={t.id}
              role="tab"
              aria-selected={active}
              aria-controls={`panel-${t.id}`}
              onClick={() => setTab(t.id)}
              className={cls(
                "relative inline-flex items-center gap-2 px-3.5 py-2 rounded-xl text-[12.5px] font-medium transition-colors",
                active ? "text-cream" : "text-ink-700 hover:text-ink-950",
              )}
            >
              {active ? (
                <motion.span
                  layoutId="int-tab-indicator"
                  className="absolute inset-0 rounded-xl bg-ink-950 shadow-[0_1px_2px_rgba(0,0,0,0.10),0_6px_14px_-6px_rgba(0,0,0,0.18)]"
                  transition={{ type: "spring", stiffness: 380, damping: 32 }}
                />
              ) : null}
              <span className="relative inline-flex items-center gap-1.5">
                {t.icon}
                {t.label}
                {t.badge ? (
                  <span className={cls(
                    "ml-0.5 px-1.5 py-0.5 rounded-full text-[10px] font-mono tabular leading-none",
                    active ? "bg-cream/15 text-cream" : "bg-cream-dark text-ink-700",
                  )}>
                    {t.badge}
                  </span>
                ) : null}
              </span>
            </button>
          );
        })}
      </nav>

      {/* Active-tab subtitle — calm, Apple-y */}
      <div className="px-1 -mt-2">
        <span className="text-[11.5px] text-ink-500 inline-flex items-center gap-1.5">
          <span className="w-1.5 h-1.5 rounded-full bg-foyr animate-pulse" />
          {current.label} · {current.sub}
        </span>
      </div>

      {/* Tab panel — crossfade between sections */}
      <AnimatePresence mode="wait">
        <motion.section
          key={tab}
          id={`panel-${tab}`}
          role="tabpanel"
          aria-labelledby={tab}
          initial={{ opacity: 0, y: 6 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -4 }}
          transition={{ duration: 0.22, ease: [0.16, 1, 0.3, 1] }}
        >
          {tab === "onboard"  ? <Onboard />            : null}
          {tab === "flows"    ? <FlowGallery />        : null}
          {tab === "mesh"     ? <AgenticMesh />        : null}
          {tab === "ontology" ? <OntologyMap />        : null}
          {tab === "topology" ? <IntegrationFlowGraph /> : null}
        </motion.section>
      </AnimatePresence>
    </div>
  );
}
